import { createLogger } from "@/utils/createLogger";

import { IS_DEV } from "@/data/constants";
import { TConfiguredTracker } from "@/tracking/createTrackerConfig";
import { TDeepPartial } from "@/types/utils";
import { mergeContentOverride } from "@/utils/mergeContentOverride";
import { TPricingEvent } from "./events.share.pricing";
import { TPricingPageConfigBase } from "./types.share.pricing";

// ─── Types ───────────────────────────────────────────────────────────────────

type TPricingServiceDeps = {
  track: TConfiguredTracker<TPricingEvent>;
};

// What the shared layer needs from a version's service singleton. `Content` is
// deliberately absent: shared logic never reads copy.
export interface TPricingServiceLike {
  readonly track: TConfiguredTracker<TPricingEvent>;
  readonly isUserLoggedIn: boolean;
  readonly logger: ReturnType<typeof createLogger>;
  readonly Config: TPricingPageConfigBase;
}

/** The static layers a version brings to the factory. */
type TPricingStaticLayers<
  TConfig extends TPricingPageConfigBase,
  TContent,
  TRegion extends string,
> = {
  /** Behaviour and structure — flags, defaults, redirects, ids. */
  config: TConfig;
  // Every user-facing string, merged from that version's `content/<locale>`
  // JSON. `undefined` for a version with no content layer yet.
  content: TContent;
  // Per-region partials, deep-merged over `content` once `syncRegion` names the
  // region. `NoInfer` keeps `TContent` coming from `content` alone.
  contentOverrides?: Partial<Record<TRegion, TDeepPartial<NoInfer<TContent>>>>;
};

// ─── Factory ─────────────────────────────────────────────────────────────────

// One service singleton per pricing version, created at module load in that
// version's `service.pricing.ts`; `index.tsx` calls `init` and the syncs.
export function createPricingService<
  TConfig extends TPricingPageConfigBase,
  TContent,
  TRegion extends string = string,
>({
  config,
  content,
  contentOverrides,
}: TPricingStaticLayers<TConfig, TContent, TRegion>) {
  let _initialized = false;

  // private state
  let _track: TConfiguredTracker<TPricingEvent>;
  let _isUserLoggedIn = false;
  let _tab: string | undefined;
  let _eventContext: Record<string, unknown> | undefined;
  let _enabledLogging = IS_DEV;
  let _region: TRegion | undefined;
  let _content = content;

  /** One merge per region for the page's lifetime, not one per render. */
  const _contentByRegion = new Map<TRegion, TContent>();

  function init(deps: TPricingServiceDeps) {
    if (_initialized) return;
    _initialized = true;

    _track = deps.track;
  }

  // Outside `init()` because auth resolves after the first render: `index.tsx`
  // re-syncs every render, while `init()` fires once.
  function syncAuth(isUserLoggedIn: boolean) {
    _isUserLoggedIn = isUserLoggedIn;
  }

  // The active tab changes while the page is mounted, so it can't live in the
  // tracker's static `defaultProperties`.
  function syncTab(tab: string) {
    _tab = tab;
  }

  /** Page-level properties every event carries. A version that never calls this is unaffected. */
  function syncEventContext(context: Record<string, unknown>) {
    _eventContext = context;
  }

  function resolveRegionalContent(region: TRegion): TContent {
    const cached = _contentByRegion.get(region);
    if (cached) return cached;

    const merged = mergeContentOverride(content, contentOverrides?.[region]);
    _contentByRegion.set(region, merged);

    return merged;
  }

  // Read Content inside components: a module-level const would capture the
  // default before the region is known. Unmapped regions keep the default.
  function syncRegion(region?: TRegion) {
    if (region === _region) return;

    _region = region;
    _content = region ? resolveRegionalContent(region) : content;
  }

  return {
    init,
    syncAuth,
    syncTab,
    syncRegion,
    syncEventContext,
    // Wraps the injected tracker so the tab and page context ride along on
    // every event; call-site properties win over both.
    get track(): TConfiguredTracker<TPricingEvent> {
      return (event, properties) =>
        _track(event, {
          ..._eventContext,
          ...(_tab ? { tab: _tab } : undefined),
          ...properties,
        });
    },
    get isUserLoggedIn() {
      return _isUserLoggedIn;
    },
    /** Static page config — available before `init()` is called. */
    Config: config,
    // The module's only read point for its content JSON, returning the copy for
    // the region last passed to `syncRegion`.
    get Content(): TContent {
      return _content;
    },
    /** Module logger — available before `init()` is called. */
    logger: createLogger("PRICING", _enabledLogging),
  };
}

export type TPricingService = TPricingServiceLike;
